import type { PayexThunkAction } from 'store/configureStore';
import { trackEvent } from './tracker';

export const trackAcceptOfferRedirection =
  ({
    productType,
    sessionId,
    currency,
    decision: { action: decisionAction, fee },
  }: {
    productType?: string;
    sessionId: string;
    currency: string;
    decision: { action: 'charge' | 'surcharge' | 'block'; fee: number };
  }): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'cta';
    const action = 'Click on Accept Offer';
    const name = 'click_accept_offer_redirection';
    const value = 'click_accept_offer_redirection';
    const requester = window.requester;
    const props = {
      action,
      category,
      value,
      requester,
      productType,
      checkoutSessionId: sessionId,
      currency,
      decision: decisionAction,
      fee,
    };

    return trackEvent(tracker, name, props);
  };

export const trackChangeCardRedirection =
  ({
    productType,
    sessionId,
  }: {
    productType?: string;
    sessionId: string;
  }): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'cta';
    const action = 'Click on Change Card';
    const name = 'click_change_card_redirection';
    const value = 'click_change_card_redirection';
    const requester = window.requester;
    const props = {
      action,
      category,
      value,
      requester,
      productType,
      checkoutSessionId: sessionId,
    };

    return trackEvent(tracker, name, props);
  };

export const trackChooseCurrency =
  ({
    productType,
    sessionId,
    decision: { action: decisionAction, fee },
    currency,
  }: {
    productType?: string;
    sessionId: string;
    decision: { action: 'charge' | 'surcharge' | 'block'; fee: number };
    currency: string;
  }): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'cta';
    const action = 'Click on the selected currency';
    const name = 'click_choose_currency';
    const value = 'click_choose_currency';
    const requester = window.requester;
    const props = {
      action,
      category,
      value,
      requester,
      productType,
      checkoutSessionId: sessionId,
      decision: decisionAction,
      fee,
      currency,
    };

    return trackEvent(tracker, name, props);
  };

export const trackEditCurrency =
  ({
    productType,
    sessionId,
  }: {
    productType?: string;
    sessionId: string;
  }): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'cta';
    const action = 'Click on the edit currency';
    const name = 'click_edit_currency';
    const value = 'click_edit_currency';
    const requester = window.requester;
    const props = {
      action,
      category,
      value,
      requester,
      productType,
      checkoutSessionId: sessionId,
    };

    return trackEvent(tracker, name, props);
  };

export const trackHoverSurchargeTooltip =
  ({
    productType,
    sessionId,
  }: {
    productType?: string;
    sessionId: string;
  }): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'cta';
    const action = 'Hover on the surcharge tooltip';
    const name = 'hover_surcharge_tooltip';
    const value = 'hover_surcharge_tooltip';
    const requester = window.requester;
    const props = {
      action,
      category,
      value,
      requester,
      productType,
      checkoutSessionId: sessionId,
    };

    return trackEvent(tracker, name, props);
  };

export const trackExpandPriceBreakdown =
  ({
    productType,
    sessionId,
    priceType,
  }: {
    productType?: string;
    sessionId: string;
    priceType: 'price' | 'fx';
  }): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'cta';
    const action = 'Expand the price breakdown';
    const name = 'expands_price_breakdown';
    const value = 'expands_price_breakdown';
    const requester = window.requester;
    const props = {
      action,
      category,
      value,
      requester,
      productType,
      checkoutSessionId: sessionId,
      priceType,
    };

    return trackEvent(tracker, name, props);
  };

export const trackClickPay =
  ({
    productType,
    sessionId,
  }: {
    productType?: string;
    sessionId: string;
  }): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'cta';
    const action = 'Click on the pay button';
    const name = 'click_pay_button';
    const value = 'click_pay_button';
    const requester = window.requester;
    const props = {
      action,
      category,
      value,
      requester,
      productType,
      checkoutSessionId: sessionId,
    };

    return trackEvent(tracker, name, props);
  };

export const trackCardElementEventForSurcharge =
  ({
    type,
    content,
    sessionId,
  }: {
    type: string;
    content: unknown;
    sessionId: string;
  }): PayexThunkAction =>
  (_, getState, { tracker }) => {
    const state = getState();
    const { recipient, sender_country: country } = state.ui.fieldValues;

    const category = 'cardElement';
    const action = 'event';
    const name = 'surcharge_card_element_event';

    const props = {
      category,
      action,
      recipient,
      country,
      value: type,
      content,
      checkoutSessionId: sessionId,
    };

    return trackEvent(tracker, name, props);
  };

export const trackTryAgain =
  ({ sessionId }: { sessionId: string }): PayexThunkAction =>
  (_, getState, { tracker }) => {
    const state = getState();
    const { recipient, sender_country: country } = state.ui.fieldValues;

    const category = 'cta';
    const action = 'click option';
    const name = 'click_try_again_surcharge_redirection_form';
    const value = 'click_try_again_surcharge_redirection_form';

    const props = {
      category,
      action,
      recipient,
      country,
      value,
      checkoutSessionId: sessionId,
    };

    return trackEvent(tracker, name, props);
  };

export const trackChangePaymentMethod =
  ({ sessionId }: { sessionId: string }): PayexThunkAction =>
  (_, getState, { tracker }) => {
    const state = getState();
    const { recipient, sender_country: country } = state.ui.fieldValues;

    const category = 'cta';
    const action = 'click option';
    const name = 'click_change_method_surcharge_redirection_form';
    const value = 'click_change_method_surcharge_redirection_form';

    const props = {
      category,
      action,
      recipient,
      country,
      value,
      checkoutSessionId: sessionId,
    };

    return trackEvent(tracker, name, props);
  };
